import { MAX_PRODUCT_TEXT_OPTIONS } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { useToggleSet } from '@/shared/lib';
import { Button, cn } from '@/shared/ui';

const EMPTY_WARNING = '하나도 허용하지 않으면 고객이 글자를 넣은 디자인을 저장할 수 없습니다.';

interface TextOptionAllowOption {
  readonly id: string;
  readonly label: string;
  /** 칩 앞에 그릴 견본 색(#RRGGBB) — 잉크 색상만 쓴다. */
  readonly swatch?: string;
}

interface TextOptionAllowFormProps {
  /** 고를 수 있는 마스터 항목 — 서버가 준 순서 그대로 칩을 그리고 저장한다. */
  readonly options: readonly TextOptionAllowOption[];
  /** 저장된 허용 id — 마운트할 때만 읽는다. 저장 뒤 다시 불러온 값은 부모가 key 를 바꿔 반영한다. */
  readonly initialIds: readonly string[];
  /** '폰트'·'잉크 색상' — 칩 묶음 이름·저장 버튼·확인 문구에 쓴다. */
  readonly itemLabel: string;
  /** 허용을 해제할 때 확인창에 덧붙이는 영향(정책 docs/products/custom-policy.md §3-4). */
  readonly removalImpact: string;
  readonly isSaving: boolean;
  readonly onSave: (ids: string[]) => void;
}

/**
 * 상품의 허용 폰트·잉크 색상 칩 폼 — 인쇄 방식 섹션과 같은 토글 칩(aria-pressed). 허용을 해제하면 저장 전에
 * 확인창으로 영향을 알린다. 저장할 id 는 목록과 맞춰 고른다.
 * - 골라 둔 항목이 다시 불러온 목록에서 빠졌으면(다른 화면에서 지움) 보내지 않는다 — 지운 id 가 섞이면 서버가 400.
 * - 저장된 허용 항목이 아직 목록에 없으면(목록 캐시가 새 항목을 모름) 칩이 없어 고를 수 없으니 그대로 보낸다 —
 *   빼면 확인창 없이 해제된다. 단 목록에 있을 때 해제해 둔 항목은 선택에 없으니 다시 붙이지 않는다.
 */
export const TextOptionAllowForm = ({
  options,
  initialIds,
  itemLabel,
  removalImpact,
  isSaving,
  onSave,
}: TextOptionAllowFormProps): ReactElement => {
  const { selected, toggle } = useToggleSet(initialIds);

  const initial = new Set(initialIds);
  const listed = new Set(options.map((option) => option.id));
  const chosen = options.filter((option) => selected.has(option.id));
  const removed = options.filter((option) => initial.has(option.id) && !selected.has(option.id));
  // 저장된 id ∩ 선택 − 목록. 선택 집합 전체(− 목록)를 쓰면 고른 뒤 목록에서 빠진 id 까지 다시 붙는다.
  const unlistedSaved = initialIds.filter((id) => !listed.has(id) && selected.has(id));
  const ids = [...chosen.map((option) => option.id), ...unlistedSaved];
  const isOverLimit = ids.length > MAX_PRODUCT_TEXT_OPTIONS;

  const handleSave = (): void => {
    if (removed.length > 0) {
      const names = removed.map((option) => `"${option.label}"`).join(', ');

      if (
        !window.confirm(
          `허용 ${itemLabel}에서 ${names} 을(를) 해제하시겠습니까?\n\n${removalImpact}`,
        )
      ) {
        return;
      }
    }

    onSave(ids);
  };

  return (
    <div className="flex flex-col gap-3">
      <fieldset>
        {/* 제목은 섹션 카드가 이미 보인다 — 칩 묶음의 이름은 화면 낭독기에만 준다. */}
        <legend className="sr-only">{`허용 ${itemLabel}`}</legend>
        <div className="flex flex-wrap gap-2">
          {options.map((option) => {
            const isSelected = selected.has(option.id);

            return (
              <button
                key={option.id}
                type="button"
                aria-pressed={isSelected}
                onClick={() => toggle(option.id)}
                className={cn(
                  'flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition-colors',
                  isSelected
                    ? 'border-foreground bg-foreground text-background'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                )}
              >
                {/* 테두리를 둬 흰색 계열 잉크도 보이게 한다. 이름이 칩의 접근 이름이 되도록 견본은 숨긴다. */}
                {option.swatch ? (
                  <span
                    aria-hidden="true"
                    className="size-3 rounded-full border"
                    style={{ backgroundColor: option.swatch }}
                  />
                ) : null}
                {option.label}
              </button>
            );
          })}
        </div>
      </fieldset>

      {/* 평소에도 보일 수 있는 안내라 role=status·alert 를 쓰지 않는다 — 화면의 저장 토스트(role=status)와 겹치지 않게. */}
      {ids.length === 0 ? <p className="text-sm text-amber-700">{EMPTY_WARNING}</p> : null}

      {isOverLimit ? (
        <p className="text-sm text-destructive">
          한 상품에 {MAX_PRODUCT_TEXT_OPTIONS}개까지 허용할 수 있습니다. 지금 {ids.length}개를
          골랐습니다.
        </p>
      ) : null}

      <div>
        <Button size="sm" disabled={isOverLimit} isLoading={isSaving} onClick={handleSave}>
          {`허용 ${itemLabel} 저장`}
        </Button>
      </div>
    </div>
  );
};
